import { motion } from 'framer-motion'
import { Activity, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { Deliverable, Plan } from '@/components/Configuracao/plansTabTypes'
import {
  SUPPORT_PRICING_OPTIONS,
  formatBRL,
  resolveSupportPricingMode,
  supportPricingLabel,
} from '@/components/Configuracao/plansTabUtils'

type Props = {
  plan: Plan
  deliverables: Deliverable[]
  totalHours: number
  calculatedValue: number
  onPlanChange: (plan: Plan) => void
  onDeliverableChange: (idx: number, field: keyof Deliverable, value: string) => void
  onAddDeliverable: () => void
  onRemoveDeliverable: (idx: number) => void
}

export function PlansTabModalEscopoTab({
  plan,
  deliverables,
  totalHours,
  calculatedValue,
  onPlanChange,
  onDeliverableChange,
  onAddDeliverable,
  onRemoveDeliverable,
}: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="flex flex-col gap-4"
    >
      <div className="flex items-end justify-between">
        <div>
          <h4 className="text-[var(--text-primary)] font-semibold flex items-center gap-2 text-[14px]">
            <Activity size={18} className="text-[var(--brand-primary)]" />
            Entregas da Matriz
          </h4>
          <p className="text-[var(--text-muted)] text-[12px] mt-0.5">
            Defina o cardápio, a frequência e o esforço operacional. O preço vem da modalidade mensal.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onAddDeliverable}
          icon={<Plus size={14} />}
        >
          Adicionar Nova Linha
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[var(--glass-border)]">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="bg-[var(--glass-bg)] border-b border-[var(--glass-border)]">
              {['Item do Escopo', 'Categoria', 'Qtd/Freq', 'Esforço (h)', 'Ações'].map((label, i) => (
                <th
                  key={label}
                  className={`text-left px-4 py-3 font-medium text-[var(--font-color-label)] uppercase tracking-wider ${
                    i === 0 ? 'w-[50%]' : i === 4 ? 'text-center w-[10%]' : 'w-[10%]'
                  }`}
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deliverables.map((d, idx) => (
              <tr key={idx} className="border-b border-[var(--glass-border)] last:border-0">
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={d.name}
                    onChange={(e) => onDeliverableChange(idx, 'name', e.target.value)}
                    placeholder="Ex: Reunião mensal"
                    className="w-full bg-transparent text-[var(--text-primary)] text-[13px] outline-none placeholder:text-[var(--text-muted)] min-h-[36px] px-1"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={d.category || ''}
                    onChange={(e) => onDeliverableChange(idx, 'category', e.target.value)}
                    placeholder="Categoria"
                    className="w-full bg-transparent text-[var(--text-muted)] text-[12px] outline-none placeholder:text-[var(--text-muted)] min-h-[36px] px-1"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={d.quantity}
                    onChange={(e) => onDeliverableChange(idx, 'quantity', e.target.value)}
                    className="w-full bg-transparent text-[var(--text-primary)] text-[13px] outline-none min-h-[36px] px-1"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={d.hours === 0 ? '' : d.hours}
                    onChange={(e) => onDeliverableChange(idx, 'hours', e.target.value)}
                    className="w-full bg-transparent text-[#f5a623] font-semibold text-[13px] outline-none min-h-[36px] px-1"
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveDeliverable(idx)}
                    className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[#e5484d] hover:bg-[rgba(229,72,77,0.1)] transition-colors cursor-pointer"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {deliverables.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-[var(--text-muted)] py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Activity size={40} className="text-[var(--text-muted)] opacity-30" />
                    <span className="text-[13px] font-medium">Sua matriz para este plano ainda está vazia.</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4 px-5 py-4 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-lg">
        <div className="flex min-w-[280px] flex-col gap-2">
          <span className="text-[#27a644] font-bold text-[11px] uppercase whitespace-nowrap">Modalidade mensal</span>
          <div className="flex flex-wrap gap-2">
            {SUPPORT_PRICING_OPTIONS.map((option) => {
              const active = resolveSupportPricingMode(plan.config) === option.id
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    onPlanChange({
                      ...plan,
                      config: {
                        ...plan.config,
                        supportPricingMode: option.id,
                        monthlyFixedValue: 2000,
                        monthlyBaseValue: 500,
                        revenueSharePercent:
                          'revenueSharePercent' in option ? option.revenueSharePercent : 0,
                      },
                    })
                  }
                  className={`rounded-md border px-3 py-2 text-left text-[12px] transition-colors ${
                    active
                      ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--text-primary)]'
                      : 'border-[var(--glass-border)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <span className="block font-semibold">{option.label}</span>
                  <span className="block text-[11px]">{option.description}</span>
                </button>
              )
            })}
          </div>
          <span className="text-[var(--text-muted)] text-[11px] leading-tight">
            Valor do plano: {supportPricingLabel(plan.config)}.
          </span>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <span className="text-[10px] text-[var(--text-muted)] uppercase block leading-tight mb-0.5">
              Esforço operacional
            </span>
            <strong className="text-[#f5a623] text-[18px]">{totalHours.toFixed(2)}h</strong>
          </div>
          <div className="text-right pl-6 border-l border-[var(--glass-border)]">
            <span className="text-[10px] text-[var(--text-muted)] uppercase block leading-tight mb-0.5">
              Valor base mensal
            </span>
            <strong className="text-[#27a644] text-[20px] whitespace-nowrap">{formatBRL(calculatedValue)}</strong>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
